import { useEffect, useState } from 'react';
import { loadSubscriptions, saveSubscriptions } from '../../utils/subscriptionApi';
import '../../styles/subscriptions.css';

const copyLabels = {
  eyebrow: 'Hero eyebrow', title: 'Hero title', description: 'Hero description',
  enquiryTitle: 'Enquiry title', enquiryDescription: 'Enquiry description',
  termsTitle: 'Terms heading', certificationTerms: 'Certification terms',
  discountTerms: 'Workshop-discount terms', interviewTerms: 'Interview preparation terms'
};
const clone = value => JSON.parse(JSON.stringify(value));

export function SubscriptionSessionGate({ authenticated, otp, onOtpChange, onSubmit, error }) {
  if (authenticated) return <Subscriptions />;
  return (
    <section className="subscription-editor" aria-labelledby="subscription-signin-title">
      <h2 id="subscription-signin-title">Admin sign in required</h2>
      <p>Use the existing admin OTP and session to edit the subscription catalogue.</p>
      <form onSubmit={onSubmit}>
        <label className="subscription-editor-field"><span>Admin OTP</span><input value={otp} onChange={event => onOtpChange(event.target.value)} type="password" inputMode="numeric" autoComplete="one-time-code" required /></label>
        {error && <p role="alert" className="subscription-feedback subscription-feedback--error">{error}</p>}
        <button type="submit" className="subscription-button">Sign in to edit subscriptions</button>
      </form>
    </section>
  );
}

export default function Subscriptions() {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [conflict, setConflict] = useState(false);
  const [confirmReload, setConfirmReload] = useState(false);
  const dirty = Boolean(draft && JSON.stringify(draft) !== JSON.stringify(saved));

  const load = async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const data = await loadSubscriptions();
      setSaved(data);
      setDraft(clone(data));
      setConflict(false);
      setConfirmReload(false);
    } catch (failure) { setError(failure.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const edit = (path, value) => {
    setDraft(current => {
      const next = clone(current);
      let target = next;
      path.slice(0, -1).forEach(key => { target = target[key]; });
      target[path[path.length - 1]] = value;
      return next;
    });
    setMessage('');
  };
  const save = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const data = await saveSubscriptions(draft);
      setSaved(data);
      setDraft(clone(data));
      setMessage(`Catalogue saved. Version ${data.version} is now available on the public page.`);
    } catch (failure) {
      setError(failure.message);
      setConflict(failure.status === 409);
    } finally { setSaving(false); }
  };
  const reload = () => {
    if (dirty) setConfirmReload(true);
    else load();
  };
  const stringField = (label, value, path, multiline = false) => (
    <label className="subscription-editor-field" key={path.join('.')}>
      <span>{label}</span>
      {multiline
        ? <textarea required maxLength={2000} rows={3} value={value} onChange={event => edit(path, event.target.value)} />
        : <input required maxLength={2000} value={value} onChange={event => edit(path, event.target.value)} />}
    </label>
  );

  return (
    <section className="subscription-editor" aria-labelledby="subscription-editor-title">
      <div className="subscription-editor-header"><div><h2 id="subscription-editor-title">Subscription catalogue</h2><p>Edit the public page without a frontend rebuild. Plan IDs, currency and monthly billing remain fixed.</p></div><button type="button" onClick={reload} disabled={loading || saving}>Reload catalogue</button></div>
      {loading && <p role="status">Loading catalogue…</p>}
      {error && <p className="subscription-feedback subscription-feedback--error" role="alert">{error}</p>}
      {message && <p className="subscription-feedback subscription-feedback--success" role="status">{message}</p>}
      {conflict && <p>Your edits are still in the form. Copy any changes you want to keep before reloading the latest version.</p>}
      {confirmReload && <div className="subscription-editor-reload" role="alert"><p>Reloading will discard your unsaved edits. Continue?</p><button type="button" disabled={loading || saving} onClick={load}>Discard edits and reload</button><button type="button" onClick={() => setConfirmReload(false)}>Keep editing</button></div>}
      {draft && <form onSubmit={save}>
        <p className="subscription-editor-meta">Version {saved.version} · Updated {new Date(saved.updatedAt).toLocaleString()} · {dirty ? 'Unsaved changes' : 'All changes saved'}</p>
        <fieldset disabled={loading || saving}>
          <legend>Page copy & pending terms</legend>
          <p>Keep unconfirmed certification details, workshop-discount conditions and interview prices explicit. This page captures enquiries, not paid activations.</p>
          <div className="subscription-editor-fields">{Object.entries(copyLabels).map(([key, label]) => stringField(label, draft.page[key], ['page', key], key.toLowerCase().includes('description') || key.includes('Terms')))}</div>
        </fieldset>
        <fieldset disabled={loading || saving}>
          <legend>Plans · INR / month</legend>
          <div className="subscription-editor-plan-grid">{draft.plans.map((plan, index) => (
            <fieldset key={plan.id}><legend>{plan.id}</legend>
              {stringField(`${plan.id} name`, plan.name, ['plans', index, 'name'])}
              {stringField(`${plan.id} description`, plan.description, ['plans', index, 'description'], true)}
              <label className="subscription-editor-field"><span>{plan.id} monthly price (INR)</span><input type="number" required min="0" step="0.01" value={plan.price} onChange={event => edit(['plans', index, 'price'], event.target.value === '' ? '' : Number(event.target.value))} /></label>
            </fieldset>
          ))}</div>
        </fieldset>
        {draft.featureGroups.map((group, groupIndex) => (
          <fieldset key={group.id} disabled={loading || saving}>
            <legend>{group.label}</legend>
            {stringField(`${group.id} group heading`, group.label, ['featureGroups', groupIndex, 'label'])}
            {group.features.map((feature, featureIndex) => (
              <div className="subscription-editor-feature" key={feature.id}>
                {stringField(`${feature.id} label`, feature.label, ['featureGroups', groupIndex, 'features', featureIndex, 'label'])}
                <div className="subscription-editor-values">{draft.plans.map(plan => {
                  const path = ['featureGroups', groupIndex, 'features', featureIndex, 'values', plan.id];
                  const label = `${feature.label} — ${plan.name}`;
                  return <label className="subscription-editor-field" key={plan.id}><span>{label}</span>
                    {feature.type === 'included'
                      ? <select value={String(feature.values[plan.id])} onChange={event => edit(path, event.target.value === 'true')}><option value="true">Included</option><option value="false">Not included</option></select>
                      : feature.type === 'pending'
                        ? <input value={feature.values[plan.id]} readOnly />
                        : <input type="number" min="0" max={feature.type === 'percentage' ? 100 : undefined} step={feature.type === 'percentage' ? 'any' : '1'} required value={feature.values[plan.id]} onChange={event => edit(path, event.target.value === '' ? '' : Number(event.target.value))} />}
                  </label>;
                })}</div>
              </div>
            ))}
          </fieldset>
        ))}
        <div className="subscription-editor-actions"><button className="subscription-button" type="submit" disabled={loading || saving || !dirty || conflict}>{saving ? 'Saving…' : 'Save catalogue'}</button><button type="button" disabled={loading || saving || !dirty} onClick={() => { setDraft(clone(saved)); setError(''); setMessage('Edits cancelled.'); setConfirmReload(false); }}>Cancel edits</button><span>Changes become public only after a successful save.</span></div>
      </form>}
    </section>
  );
}
